import React, { useState, useRef, useEffect } from "react";
import { Send, Bot, User, Loader2 } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useSendMessage } from "../../hooks/useChatbot";
import type { ChatMessage } from "../../services/api/chat.service";

interface ChatInterfaceProps {
  startupId: string;
  isOpen: boolean;
  onClose: () => void;
}

export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  startupId,
  isOpen,
  onClose,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  
  const sendMessageMutation = useSendMessage(startupId);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Add welcome message on first open
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      const welcomeMessage: ChatMessage = {
        role: "assistant",
        content: "Hello! I'm your AI Financial Advisor. I have access to all your startup's financial data including expenses, revenues, and projections. How can I help you analyze your financial situation today?",
        timestamp: new Date().toISOString(),
      };
      setMessages([welcomeMessage]);
    }
  }, [isOpen, messages.length]);

  const handleSendMessage = async () => {
    if (!inputValue.trim() || sendMessageMutation.isPending) return;

    const userMessage: ChatMessage = {
      role: "user",
      content: inputValue.trim(),
      timestamp: new Date().toISOString(),
    };

    // Add user message immediately
    setMessages(prev => [...prev, userMessage]);
    setInputValue("");

    try {
      const response = await sendMessageMutation.mutateAsync({
        message: userMessage.content,
        conversationHistory: messages,
      });

      // Add assistant response
      const assistantMessage: ChatMessage = {
        role: "assistant",
        content: response.response,
        timestamp: response.timestamp,
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error("Failed to send message:", error);
      // Add error message
      const errorMessage: ChatMessage = {
        role: "assistant",
        content: "I apologize, but I'm experiencing technical difficulties. Please try again in a moment.",
        timestamp: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMessage]);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed bottom-24 right-4 z-50 flex h-[32rem] w-96 flex-col overflow-hidden rounded-[20px] bg-surface ring-1 ring-white/[0.12] shadow-glow lg:bottom-6 lg:h-[50rem] lg:w-[48rem]">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-white/10 bg-surface-elevated p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-brand/20 ring-1 ring-brand/30">
            <Bot className="h-5 w-5 text-brand" />
          </div>
          <div>
            <h3 className="font-bold text-white">AI Financial Advisor</h3>
            <p className="text-xs text-emerald-100/80">Expert startup financial guidance</p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="text-emerald-100/80 hover:text-white transition-colors"
        >
          ×
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.map((message, index) => (
          <div
            key={index}
            className={`flex gap-3 ${
              message.role === "user" ? "justify-end" : "justify-start"
            }`}
          >
            {message.role === "assistant" && (
              <div className="w-8 h-8 bg-brand/15 rounded-full flex items-center justify-center flex-shrink-0">
                <Bot className="h-4 w-4 text-brand" />
              </div>
            )}
            
            <div
              className={`${
                message.role === "user" 
                  ? "max-w-[75%] bg-brand text-surface" 
                  : "max-w-[90%] bg-white/[0.06] text-white"
              } p-3 rounded-lg`}
            >
              {message.role === "assistant" ? (
                <div className="text-sm prose prose-sm max-w-none">
                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      // Customize markdown components for chat
                      p: ({ children }) => <p className="mb-2 last:mb-0">{children}</p>,
                      ul: ({ children }) => <ul className="mb-2 last:mb-0 pl-4">{children}</ul>,
                      ol: ({ children }) => <ol className="mb-2 last:mb-0 pl-4">{children}</ol>,
                      li: ({ children }) => <li className="mb-1">{children}</li>,
                      strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                      em: ({ children }) => <em className="italic">{children}</em>,
                      code: ({ children }) => (
                        <code className="bg-white/10 px-1 py-0.5 rounded text-xs font-mono">
                          {children}
                        </code>
                      ),
                      pre: ({ children }) => (
                        <pre className="bg-white/10 p-2 rounded text-xs overflow-x-auto">
                          {children}
                        </pre>
                      ),
                      table: ({ children }) => (
                        <div className="overflow-x-auto mb-4 mt-2">
                          <table className="min-w-full border-collapse border border-white/20 text-xs bg-surface">
                            {children}
                          </table>
                        </div>
                      ),
                      thead: ({ children }) => (
                        <thead className="bg-white/[0.06]">{children}</thead>
                      ),
                      tbody: ({ children }) => (
                        <tbody>{children}</tbody>
                      ),
                      tr: ({ children }) => (
                        <tr className="border-b border-white/10">{children}</tr>
                      ),
                      th: ({ children }) => (
                        <th className="border border-white/20 px-3 py-2 text-left font-semibold bg-surface-elevated">
                          {children}
                        </th>
                      ),
                      td: ({ children }) => (
                        <td className="border border-white/20 px-3 py-2">
                          {children}
                        </td>
                      ),
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                </div>
              ) : (
                <p className="text-sm whitespace-pre-wrap">{message.content}</p>
              )}
              <p className="text-xs mt-1 opacity-70">
                {new Date(message.timestamp).toLocaleTimeString()}
              </p>
            </div>

            {message.role === "user" && (
              <div className="w-8 h-8 bg-white/[0.06] rounded-full flex items-center justify-center flex-shrink-0">
                <User className="h-4 w-4 text-white/65" />
              </div>
            )}
          </div>
        ))}
        
        {sendMessageMutation.isPending && (
          <div className="flex gap-3 justify-start">
            <div className="w-8 h-8 bg-brand/15 rounded-full flex items-center justify-center flex-shrink-0">
              <Bot className="h-4 w-4 text-brand" />
            </div>
            <div className="bg-white/[0.06] text-white p-3 rounded-lg">
              <div className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Analyzing your financial data...</span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="p-4 border-t border-white/10">
        <div className="flex gap-2">
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask about your finances..."
            disabled={sendMessageMutation.isPending}
            className="sh-input flex-1"
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || sendMessageMutation.isPending}
            className="rounded-[14px] bg-brand px-4 py-2 text-surface transition-colors hover:bg-brand-muted disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-white/45 mt-2">
          Ask about cash flow, runway, expenses, revenue projections, or funding strategies.
        </p>
      </div>
    </div>
  );
};
